<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import HeartIcon from 'vue-material-design-icons/Heart.vue';
import HeartOutlineIcon from 'vue-material-design-icons/HeartOutline.vue';
import type { FavoriteCountGetResponse } from '~~/server/api/kiwi_user/recipes/favorite_count';
import { useFavorite } from '~/composables/useFavorite';

const HEART_ICON_SIZE = 24;

const props = defineProps<{
  recipeId: number;
}>();

const { isFavorited, loadFavorites, addFavorite, removeFavorite } =
  useFavorite();

const isActive = computed(() => isFavorited(props.recipeId).value);

// NOTE: 表示用のお気に入り数
// nullはまだ取得していない状態を表す
const displayCount = ref<number | null>(null);

// NOTE: 初期化が完了したら表示
const isReady = computed(() => displayCount.value !== null);

// NOTE: mount時にlocalStorageとAPIの両方から初期化
onMounted(async () => {
  // NOTE: localStorageからお気に入りIDを読み込む
  loadFavorites();

  // NOTE: APIから最新のお気に入り数を取得
  try {
    const response = await $fetch<FavoriteCountGetResponse>(
      '/api/kiwi_user/recipes/favorite_count',
      {
        params: { recipe_id: props.recipeId },
      },
    );
    displayCount.value = response.favored_count;
  } catch {
    // NOTE: エラー時は0として扱う
    displayCount.value = 0;
  }
});

/**
 * お気に入り数を表示用にフォーマットする
 * 1000以上は "1K", "1.2K" 形式で表示
 */
function formatCount(count: number | null): string {
  // NOTE: まだ取得されていない場合は「-」を表示
  if (count === null) {
    return '-';
  }
  if (count >= 1000) {
    const kCount = count / 1000;
    // 小数点以下がある場合は1桁まで表示
    if (kCount % 1 === 0) {
      return `${kCount.toFixed(0)}K`;
    }
    return `${kCount.toFixed(1)}K`;
  }
  return count.toString();
}

const formattedCount = computed(() => formatCount(displayCount.value));

// Bounceエフェクト用の状態
const isBouncing = ref(false);

async function toggleFavorite() {
  const isAlreadyActive = isActive.value;

  if (isAlreadyActive) {
    const newCount = await removeFavorite(props.recipeId);
    // NOTE: APIから返却された正確な値で更新
    if (newCount !== null) {
      displayCount.value = newCount;
    }
  } else {
    const newCount = await addFavorite(props.recipeId);
    // NOTE: APIから返却された正確な値で更新
    if (newCount !== null) {
      displayCount.value = newCount;
    }
    // お気に入り追加時のみbounceエフェクトを発火
    isBouncing.value = true;
  }
}
</script>

<template>
  <button
    v-if="isReady"
    type="button"
    class="rounded-full bg-white w-16 h-16 flex flex-col items-center justify-center shadow hover:shadow-lg hover:-translate-y-0.5 active:scale-95 active:shadow transition-all duration-200"
    :aria-label="isActive ? 'お気に入りから削除' : 'お気に入りに追加'"
    @click="toggleFavorite"
  >
    <HeartIcon
      v-if="isActive"
      :size="HEART_ICON_SIZE"
      class="text-pink-400"
      :class="{ 'animate-bounce-heart': isBouncing }"
      @animationend="isBouncing = false"
    />
    <HeartOutlineIcon v-else :size="HEART_ICON_SIZE" class="text-gray-400" />
    <span class="text-xs font-bold">{{ formattedCount }}</span>
  </button>
</template>

<style scoped>
@keyframes bounce-heart {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.4);
  }
  50% {
    transform: scale(0.9);
  }
  75% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}

.animate-bounce-heart {
  animation: bounce-heart 0.4s ease-out;
}
</style>
